import { shuffle } from 'lodash';
import useZStore, { getCommands, getFlags } from './useZStore';
import { useState, useEffect, useMemo } from 'react';
import { Example } from '@/types/Example';
import { AnswerLogEntry } from '@/components/QuizzQuestion';
import { BaseCommand } from '@/types/BaseCommand';

interface QuizzVals {
  rightAnswer: string;
  question: string;
  answers: string[];
  isValid: boolean;
}

export interface FullQuizzVals extends QuizzVals {
  exampleToQuizz: Example | null;
}

const defaultQuizzVals = {
  rightAnswer: '',
  question: '',
  answers: [] as string[],
  isValid: false,
};

const getCommandQuizzVals = (
  example: string,
  allPossibleAnswers: string[],
): QuizzVals => {
  const rightAnswer = example.match(/\s\S+\s*/)?.[0].trim();
  if (!rightAnswer) {
    return defaultQuizzVals;
  }

  const question = example.replace(/\s\S+\s*/, ' _______ ');
  const wrongAnswers = allPossibleAnswers.filter(
    (command) => command !== rightAnswer,
  );

  const threeWrongAnswers = shuffle(wrongAnswers).slice(0, 3);
  const answers = shuffle([...threeWrongAnswers, rightAnswer]);
  return {
    rightAnswer,
    question,
    answers,
    isValid: true,
  };
};

const getFlagQuizzVals = (
  example: string,
  longFlags: string[],
  shortFlags: string[],
): QuizzVals => {
  const rightAnswer = example.match(/-\S+\s*/)?.[0].trim();
  if (!rightAnswer) {
    return defaultQuizzVals;
  }

  const question = example.replace(/-\S+\s*/, ' _______ ');
  const isFlagLong = rightAnswer.match(/^--\S+/);
  const allPossibleAnswers = isFlagLong ? longFlags : shortFlags;
  const wrongAnswers = allPossibleAnswers.filter(
    (answer) => answer !== rightAnswer,
  );
  const threeWrongAnswers = shuffle(wrongAnswers).slice(0, 3);
  const answers = shuffle([...threeWrongAnswers, rightAnswer]);
  return {
    rightAnswer,
    question,
    answers,
    isValid: true,
  };
};

const getNextExampleToQuizz = (
  usableExamples: Example[],
  isCommandQuizz: boolean,
) => {
  while (true) {
    const randomIndex = Math.floor(Math.random() * usableExamples.length);
    const proposedExample = usableExamples[randomIndex];
    const exampleHasFlag = proposedExample.example.match(/-\w+/g);
    if (isCommandQuizz || exampleHasFlag) {
      return proposedExample;
    }
  }
};

const getQuizzVals = (
  exampleToQuizz: Example | null,
  isCommandQuizz: boolean,
  nakedCommands: string[],
  longFlags: string[],
  shortFlags: string[],
) => {
  if (!exampleToQuizz) {
    return defaultQuizzVals;
  }
  if (isCommandQuizz) {
    const quizzVals = getCommandQuizzVals(
      exampleToQuizz.example,
      nakedCommands,
    );

    return quizzVals;
  }

  const quizzVals = getFlagQuizzVals(
    exampleToQuizz.example,
    longFlags,
    shortFlags,
  );
  return quizzVals;
};

const createQuestion = (
  exampleToQuizz: Example | null,
  flags: string[],
  baseCommands: BaseCommand[],
  isCommandQuestion: boolean,
) => {
  // Only flags that start with --, use aregex
  const longFlags = flags.filter((flag) => flag.match(/^--\S+/));
  // Only flags that start with exactly one -
  const shortFlags = flags.filter((flag) => flag.match(/^-[^-].*/));
  const nakedCommands = baseCommands.map((command) =>
    command.command.replace(/^\S+\s*/, ''),
  );

  const quizzVals = getQuizzVals(
    exampleToQuizz,
    isCommandQuestion,
    nakedCommands,
    longFlags,
    shortFlags,
  );
  return quizzVals;
};

const useCreateQuizzQuestion = (
  unlearnedExamples: Example[],
  answeredExamples: AnswerLogEntry[],
  unusableExamples: Example[],
  isCommandQuizz: boolean,
): FullQuizzVals => {
  const baseCommands = useZStore(getCommands());
  const flags = useZStore(getFlags());
  // Only flags that start with --, use aregex
  const [exampleToQuizz, setExampleToQuizz] = useState<Example | null>(null);

  const answeredExampleIds = answeredExamples.map(
    (example) => example.exampleId,
  );
  const unansweredExamples = unlearnedExamples.filter(
    (example) => !answeredExampleIds.includes(example.id),
  );
  const usableExamples = unansweredExamples.filter(
    (example) => !unusableExamples.includes(example),
  );

  const quizzVals = useMemo(
    () => createQuestion(exampleToQuizz, flags, baseCommands, isCommandQuizz),
    [exampleToQuizz, isCommandQuizz, flags, baseCommands],
  );

  // Reset exampleToQuizz when a new example is answered
  useEffect(() => {
    setExampleToQuizz(null);
  }, [answeredExamples]);

  // Set exampleToQuizz when there is no example to quizz
  useEffect(() => {
    if (usableExamples.length && !exampleToQuizz) {
      // random example
      setExampleToQuizz(getNextExampleToQuizz(usableExamples, isCommandQuizz));
    }
  }, [usableExamples, exampleToQuizz, isCommandQuizz]);

  return { exampleToQuizz, ...quizzVals };
};

export default useCreateQuizzQuestion;
